import * as React from "react";

export type DockMode = "idle" | "record" | "fullscreen";

type UseDockModeArgs = {
    isFullScreenRecordingMode: boolean;
    setIsFullScreenRecordingMode: (v: boolean) => void;
};

type UseDockModeReturn = {
    dockMode: DockMode;

    // derived booleans (handy)
    isFullscreen: boolean;
    isRecord: boolean;
    isIdle: boolean;

    // actions
    openRecordDock: () => void;
    closeRecordDock: () => void;
    enterFullscreen: () => void;
    exitFullscreen: () => void;
    resetToIdle: () => void;
};

export function useDockMode({
    isFullScreenRecordingMode,
    setIsFullScreenRecordingMode,
}: UseDockModeArgs): UseDockModeReturn {
    const [dockMode, setDockMode] = React.useState<DockMode>("idle");

    // ✅ Single source of truth: fullscreen flag controls dockMode
    React.useEffect(() => {
        if (isFullScreenRecordingMode) {
            setDockMode("fullscreen");
        } else {
            // if you exit fullscreen, default back to idle (not record)
            // (prevents "stale record dock" after fullscreen)
            setDockMode("idle");
        }
    }, [isFullScreenRecordingMode]);

    const openRecordDock = React.useCallback(() => {
        // Don't allow record mode if fullscreen is active
        if (isFullScreenRecordingMode) return;
        setDockMode("record");
    }, [isFullScreenRecordingMode]);

    const closeRecordDock = React.useCallback(() => {
        if (isFullScreenRecordingMode) return;
        setDockMode("idle");
    }, [isFullScreenRecordingMode]);

    const enterFullscreen = React.useCallback(() => {
        // ✅ Set dockMode synchronously in the same render — don't wait for the useEffect hop
        setDockMode("fullscreen");
        setIsFullScreenRecordingMode(true);
    }, [setIsFullScreenRecordingMode]);

    const exitFullscreen = React.useCallback(() => {
        // ✅ Set dockMode synchronously in the same render
        setDockMode("idle");
        setIsFullScreenRecordingMode(false);
    }, [setIsFullScreenRecordingMode]);

    const resetToIdle = React.useCallback(() => {
        setIsFullScreenRecordingMode(false);
        setDockMode("idle");
    }, [setIsFullScreenRecordingMode]);

    return {
        dockMode,
        isFullscreen: dockMode === "fullscreen",
        isRecord: dockMode === "record",
        isIdle: dockMode === "idle",
        openRecordDock,
        closeRecordDock,
        enterFullscreen,
        exitFullscreen,
        resetToIdle,
    };
}
